/**
 * PairingPanel.tsx — v2.4.0
 *
 * THE ONE THING THIS PAGE MUST NEVER FAIL AT: getting a pairing code into a
 * tech's hands. Lyndon had to mint a code with SQL once. Never again.
 *
 *   pick employee -> pick TTL -> BIG code -> live countdown -> copy
 *   outstanding codes are listed and revocable, so a code read out to the wrong
 *   person is killed in one click.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  ContentCopy as CopyIcon, DeleteOutline as RevokeIcon, Refresh as RefreshIcon,
  VpnKey as KeyIcon,
} from '@mui/icons-material';
import { devicesApi } from '../../services/api';

export interface Employee { id: number; name: string; email: string; device_id?: string | null; }

interface Code {
  code: string; user_id: number; employee_name: string;
  expires_at: string; created_by?: string; created_at?: string;
}

const TTL_CHOICES = [5, 15, 30, 60, 120];

/** mm:ss left on a code — recomputed every second, so the dispatcher can see it
 *  die rather than reading a stale code down the phone. */
function useCountdown(expiresAt?: string | null) {
  const [left, setLeft] = useState<number>(0);
  useEffect(() => {
    if (!expiresAt) { setLeft(0); return; }
    // the API sends naive-UTC; normalise so a browser in PDT doesn't read it as local
    const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(expiresAt) ? expiresAt : `${expiresAt}Z`;
    const end = new Date(iso).getTime();
    const tick = () => setLeft(Math.max(0, Math.floor((end - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  return left;
}

function Countdown({ expiresAt, testid }: { expiresAt?: string | null; testid?: string }) {
  const left = useCountdown(expiresAt);
  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, '0');
  const dead = left <= 0;
  return (
    <Chip
      size="small"
      data-testid={testid}
      label={dead ? 'EXPIRED' : `${mm}:${ss} left`}
      color={dead ? 'error' : left < 60 ? 'warning' : 'success'}
      variant={dead ? 'filled' : 'outlined'}
    />
  );
}

export default function PairingPanel({
  open, onClose, employees, onChanged,
}: {
  open: boolean;
  onClose: () => void;
  employees: Employee[];
  onChanged: () => void;
}) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));

  const [emp, setEmp] = useState<number | ''>('');
  const [ttl, setTtl] = useState<number>(15);
  const [issued, setIssued] = useState<any>(null);   // issue-response OR a listed code
  const [issuing, setIssuing] = useState(false);
  const [codes, setCodes] = useState<Code[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadCodes = useCallback(async () => {
    setLoading(true);
    try {
      const r = await devicesApi.listCodes();
      setCodes(r.data.codes || []);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not load outstanding codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) { setIssued(null); setEmp(''); setErr(null); setCopied(false); void loadCodes(); }
  }, [open, loadCodes]);

  const issue = async () => {
    if (!emp) return;
    setIssuing(true); setErr(null);
    try {
      const r = await devicesApi.issueCode(Number(emp), ttl);
      setIssued(r.data);
      await loadCodes();
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not issue a pairing code');
    } finally {
      setIssuing(false);
    }
  };

  const revoke = async (code: string) => {
    try {
      await devicesApi.revokeCode(code);
      if (issued?.code === code) setIssued(null);
      await loadCodes();
      onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Revoke failed');
    }
  };

  const copy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setErr('Clipboard blocked by the browser — read the code out instead.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={phone}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
        <KeyIcon color="primary" /> Pair a phone
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}

        {!issued ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pick the employee this phone belongs to. You get a single-use code — the tech opens
              Employee Tracker and types it into the setup screen. That binds the phone to that
              employee: the name here becomes the name in the app.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 220px' }}>
                <InputLabel>Employee</InputLabel>
                <Select label="Employee" value={emp} data-testid="emp-select"
                  onChange={(e) => setEmp(e.target.value as number)}>
                  {employees.map((e) => (
                    <MenuItem key={e.id} value={e.id}>
                      {e.name}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {e.email}{e.device_id ? ' • already has a device' : ''}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ width: 150 }}>
                <InputLabel>Expires in</InputLabel>
                <Select label="Expires in" value={ttl} data-testid="ttl-select"
                  onChange={(e) => setTtl(Number(e.target.value))}>
                  {TTL_CHOICES.map((t) => (
                    <MenuItem key={t} value={t}>{t >= 60 ? `${t / 60} hour${t > 60 ? 's' : ''}` : `${t} minutes`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <Typography variant="body2" color="text.secondary">Pairing code for</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
              {issued.employee?.name || issued.employee_name}
            </Typography>
            <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f1f5f9', borderRadius: 3, border: '2px dashed #94a3b8' }}>
              <Typography
                data-testid="pairing-code"
                sx={{
                  fontWeight: 900, fontFamily: 'monospace', lineHeight: 1.1,
                  letterSpacing: { xs: 6, sm: 12 },
                  fontSize: { xs: '2.75rem', sm: '4rem' },
                  userSelect: 'all',
                }}
              >
                {issued.code}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center', mt: 2 }}>
              <Countdown expiresAt={issued.expires_at} testid="issued-countdown" />
              <Button size="small" variant="outlined" startIcon={<CopyIcon />} onClick={copy} data-testid="copy-code">
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button size="small" color="error" startIcon={<RevokeIcon />} onClick={() => revoke(issued.code)}>
                Revoke
              </Button>
            </Box>
            <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
              Single use. Read it to the tech, or copy it into a text. When they enter it the phone is
              bound and gets ticket access with no password.
            </Alert>
            <Button sx={{ mt: 1 }} onClick={() => { setIssued(null); setEmp(''); }}>
              Issue another code
            </Button>
          </Box>
        )}

        {/* ---- outstanding codes: everything live, revocable in one click ---- */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Outstanding codes ({codes.length})
            </Typography>
            <IconButton size="small" onClick={loadCodes}><RefreshIcon fontSize="small" /></IconButton>
            {loading && <CircularProgress size={14} />}
          </Box>
          {codes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              None outstanding. Every code has been used, revoked or has expired.
            </Typography>
          ) : (
            <Table size="small" data-testid="codes-table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Expires</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {codes.map((c) => (
                  <TableRow key={c.code} hover data-testid="code-row">
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 800, letterSpacing: 2 }}>
                      {c.code}
                    </TableCell>
                    <TableCell>{c.employee_name}</TableCell>
                    <TableCell><Countdown expiresAt={c.expires_at} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Revoke this code now">
                        <IconButton size="small" color="error" onClick={() => revoke(c.code)}>
                          <RevokeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {!issued && (
          <Button variant="contained" data-testid="generate-code-btn" disabled={!emp || issuing}
            onClick={issue}
            sx={{ background: 'linear-gradient(180deg, #1E4A6B, #0D2233)' }}>
            {issuing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Generate code'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
